import { useCallback, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import type ChromeExtension from '@project/common/app/services/chrome-extension';
import SettingsForm from '@project/common/components/SettingsForm';
import { useLocalFontFamilies } from '@project/common/hooks';
import type { Anki } from '@project/common/anki';
import type { AsbplayerSettings, Profile } from '@project/common/settings';
import { supportedLanguages, testCard } from '@project/common/settings';
import SettingsProfileSelectMenu from '@project/common/components/SettingsProfileSelectMenu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { Theme } from '@mui/material';
import type { DictionaryProvider } from '@project/common/dictionary-db';
import type { LogProvider } from '@project/common/util/log';
import { useAnnotationTutorial } from '@project/common/hooks/use-annotation-tutorial';
import { AppExtensionGlobalStateProvider } from '@project/common/app/services/app-extension-global-state-provider';

const appTestCard = () => {
    const basePath = window.location.pathname === '/' ? '' : window.location.pathname;
    return testCard({ imageUrl: `${basePath}/assets/test-card.jpeg`, audioUrl: `${basePath}/assets/test-card.mp3` });
};

const useStyles = makeStyles<Theme>((theme) => ({
    root: {
        '& .MuiPaper-root': {
            height: '100dvh',
        },
    },
    content: {
        maxHeight: '100%',
    },
    profilesContainer: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(2),
    },
    title: {
        flexGrow: 1,
    },
}));

interface Props {
    anki: Anki;
    extension: ChromeExtension;
    open: boolean;
    dictionaryProvider: DictionaryProvider;
    logProvider: LogProvider;
    settings: AsbplayerSettings;
    scrollToId?: string;
    onSettingsChanged: (settings: Partial<AsbplayerSettings>) => void;
    onClose: () => void;
    profiles: Profile[];
    activeProfile?: string;
    onNewProfile: (name: string) => void;
    onRemoveProfile: (name: string) => void;
    onSetActiveProfile: (name: string | undefined) => void;
}

export default function SettingsDialog({
    anki,
    extension,
    open,
    dictionaryProvider,
    logProvider,
    settings,
    scrollToId,
    onSettingsChanged,
    onClose,
    ...profilesContext
}: Props) {
    const { t } = useTranslation();
    const classes = useStyles();

    const {
        updateLocalFontsPermission,
        updateLocalFonts,
        localFontsAvailable,
        localFontsPermission,
        localFontFamilies,
    } = useLocalFontFamilies();
    const handleUnlockLocalFonts = useCallback(() => {
        updateLocalFontsPermission();
        updateLocalFonts();
    }, [updateLocalFontsPermission, updateLocalFonts]);
    const globalStateProvider = useMemo(() => new AppExtensionGlobalStateProvider(extension), [extension]);
    const { inAnnotationTutorial, handleAnnotationTutorialSeen } = useAnnotationTutorial({ globalStateProvider });

    return (
        <Dialog open={open} maxWidth="md" fullWidth className={classes.root} onClose={onClose}>
            <Toolbar>
                <Typography variant="h6" className={classes.title}>
                    {t('settings.title')}
                </Typography>
                <IconButton edge="end" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Toolbar>
            <DialogContent className={classes.content}>
                <SettingsForm
                    anki={anki}
                    extensionInstalled={extension.installed}
                    extensionVersion={extension.installed ? extension.version : undefined}
                    extensionSupportsAppIntegration={extension.supportsAppIntegration}
                    extensionSupportsOverlay={extension.supportsStreamingVideoOverlay}
                    extensionSupportsSidePanel={extension.supportsSidePanel}
                    extensionSupportsOrderableAnkiFields={extension.supportsOrderableAnkiFields}
                    extensionSupportsTrackSpecificSettings={extension.supportsTrackSpecificSettings}
                    extensionSupportsSubtitlesWidthSetting={extension.supportsSubtitlesWidthSetting}
                    extensionSupportsSubtitlesWidthInPixels={extension.supportsSubtitlesWidthInPixels}
                    extensionSupportsPauseOnHover={extension.supportsPauseOnHover}
                    extensionSupportsPlaybackEngine={extension.supportsPlaybackEngine}
                    extensionSupportsAutoPauseResume={extension.supportsAutoPauseResume}
                    extensionSupportsExportCardBind={extension.supportsExportCardBind}
                    extensionSupportsPageSettings={extension.supportsPageSettings}
                    extensionSupportsDictionary={extension.supportsDictionary}
                    extensionSupportsDictionaryBrowser={extension.supportsDictionaryBrowser}
                    extensionSupportsDictionaryWaniKani={extension.supportsDictionaryWaniKani}
                    extensionSupportsDictionaryMatchAcrossScripts={extension.supportsDictionaryMatchAcrossScripts}
                    extensionSupportsSeekableTrackSetting={extension.supportsSeekableTrackSetting}
                    extensionSupportsAutoCopyableTrackSetting={extension.supportsAutoCopyableTrackSetting}
                    extensionSupportsDictionaryTokenStatusDisplayAlpha={
                        extension.supportsDictionaryTokenStatusDisplayAlpha
                    }
                    extensionSupportsDictionaryYomitanMecab={extension.supportsDictionaryYomitanMecab}
                    extensionSupportsSubtitleTrackSelectorInWebApp={extension.supportsSubtitleTrackSelectorInWebApp}
                    extensionSupportsSubtitleListCustomization={extension.supportsSubtitleListCustomization}
                    extensionSupportsUpdateLastWithSameSubtitleText={extension.supportsUpdateLastWithSameSubtitleText}
                    pageConfigs={extension.pageConfig}
                    insideApp
                    appVersionRepoPath={import.meta.env.VITE_APP_VERSION_REPO_PATH}
                    chromeKeyBinds={extension.extensionCommands}
                    onOpenChromeExtensionShortcuts={extension.openShortcuts}
                    onSettingsChanged={onSettingsChanged}
                    dictionaryProvider={dictionaryProvider}
                    logProvider={logProvider}
                    settings={settings}
                    profiles={profilesContext.profiles}
                    activeProfile={profilesContext.activeProfile}
                    scrollToId={scrollToId}
                    localFontsAvailable={localFontsAvailable}
                    localFontsPermission={localFontsPermission}
                    localFontFamilies={localFontFamilies}
                    supportedLanguages={supportedLanguages}
                    testCard={appTestCard}
                    onUnlockLocalFonts={handleUnlockLocalFonts}
                    inAnnotationTutorial={inAnnotationTutorial}
                    onAnnotationTutorialSeen={handleAnnotationTutorialSeen}
                />
            </DialogContent>
            {(!extension.installed || extension.supportsSettingsProfiles) && (
                <Box className={classes.profilesContainer}>
                    <SettingsProfileSelectMenu {...profilesContext} />
                </Box>
            )}
        </Dialog>
    );
}
